import React, { useRef } from 'react';
import styled from 'styled-components';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import CountrySelect from './CountrySelect';
import LandingNamesSelect from './LandingNamesSelect';
import TimePicker from './TimePicker';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';

export const SubmitButton = styled.button`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  background-color: #e5e7eb;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #d1d5db;
  }
`;

const TextInput = styled.input`
  padding: 8px 12px;
  font-size: 16px;
  height: 37px;
  color: #555;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  width: 100%;
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  font-size: 16px;
  color: #555;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  width: 100%;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
`;

const Preloader = styled.div`
  display: inline-block;
  margin: 0;
  vertical-align: middle;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ccc;
  border-top-color: #007bff;
  animation: spin 1s infinite linear;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  z-index: 10000;
`;

const ModalContent = styled.div`
  width: 80%;
  max-width: 1280px;
  max-height: 94vh;
  min-height: 720px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  padding: 26px 36px 30px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;

  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(360px, 1fr);
  column-gap: 32px;
  row-gap: 20px;
  margin-bottom: 24px;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const LandingFieldBlock = styled(FieldBlock)`
  align-items: flex-start;
  justify-content: flex-start;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
`;

const DateRangePickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DateRangeInput = styled.input`
  padding: 8px 12px;
  font-size: 16px;
  height: 37px;
  color: #555;
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  cursor: pointer;
  width: 100%;
`;

const DateRangePickerPopup = styled.div`
  background-color: #fff;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 9999;
`;

const PreviewNotice = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #eef2ff;
  border-radius: 6px;
  font-size: 13px;
  color: #4c51bf;
`;

const PreviewTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
`;

const PreviewTable = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 14px;
  }

  th {
    background: #f9fafb;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.02em;
    color: #6b7280;
  }

  tbody tr:nth-child(odd) {
    background: #fff;
  }

  tbody tr:nth-child(even) {
    background: #f9fafb;
  }
`;

const ImageUploadButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.2s;
  margin: 0 2px;

  &:hover {
    color: #007bff;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ImageDeleteButton = styled(ImageUploadButton)`
  &:hover {
    color: #ef4444;
  }
`;

const ImageButtonsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const PreviewEmptyState = styled.div`
  padding: 24px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  gap: 12px;
`;

const ActionsGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const CreateAbTestModal = ({
  isOpen,
  onClose,
  testName,
  onTestNameChange,
  comment,
  onCommentChange,
  selectedCountry,
  onCountryChange,
  selectedDateRange,
  onDateRangeFieldClick,
  isDateRangePickerOpen,
  onDateRangeChange,
  onTimeChange,
  dateRangePickerRef,
  landingOptions,
  selectedLandingNames,
  onLandingNamesChange,
  isPreviewDirty,
  previewStatistics,
  isPreviewLoading,
  onGetInfo,
  isCreating,
  onCreate,
  landingImages,
  onLandingImageChange,
}) => {
  const fileInputRefs = useRef({});

  if (!isOpen) {
    return null;
  }

  const getLandingKey = (landing) => {
    return landing.landing_id || landing.url || landing.name || '';
  };

  const handleImageButtonClick = (landingKey) => {
    if (!fileInputRefs.current[landingKey]) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
          onLandingImageChange(landingKey, file);
        }
        input.value = '';
      };
      fileInputRefs.current[landingKey] = input;
    }
    fileInputRefs.current[landingKey].click();
  };

  const handleImageDeleteClick = (landingKey) => {
    onLandingImageChange(landingKey, null);
  };

  return (
    <ModalOverlay
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <ModalContent onClick={(event) => event.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Create A/B Test</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FieldsGrid>
            <FieldBlock>
              <FieldLabel htmlFor="ab-test-name">Test Name</FieldLabel>
              <TextInput
                id="ab-test-name"
                type="text"
                placeholder="A/B test name"
                value={testName}
                onChange={(event) => {
                  onTestNameChange(event.target.value);
                }}
              />
            </FieldBlock>

            <FieldBlock>
              <FieldLabel>Country</FieldLabel>
              <CountrySelect
                selectedCountry={selectedCountry}
                onChange={onCountryChange}
              />
            </FieldBlock>

            <FieldBlock>
              <FieldLabel>Date Range</FieldLabel>
              <DateRangePickerWrapper ref={dateRangePickerRef}>
                <DateRangeInput
                  type="text"
                  value={`${format(selectedDateRange[0].startDate, 'dd/MM/yyyy HH:mm')} - ${format(
                    selectedDateRange[0].endDate,
                    'dd/MM/yyyy HH:mm'
                  )}`}
                  readOnly
                  onClick={onDateRangeFieldClick}
                  className="datepicker-input"
                />
                {isDateRangePickerOpen && (
                  <DateRangePickerPopup>
                    <DateRange ranges={selectedDateRange} onChange={onDateRangeChange} />
                    <div className="time-picker">
                      <TimePicker selectedDateRange={selectedDateRange} onChange={onTimeChange} />
                    </div>
                  </DateRangePickerPopup>
                )}
              </DateRangePickerWrapper>
            </FieldBlock>

            <LandingFieldBlock style={{ gridColumn: '1 / -1' }}>
              <FieldLabel>Landings</FieldLabel>
              <LandingNamesSelect
                landings={landingOptions}
                selectedLandingNames={selectedLandingNames}
                onChange={onLandingNamesChange}
              />
            </LandingFieldBlock>
          </FieldsGrid>

          {isPreviewDirty && previewStatistics.length > 0 && (
            <PreviewNotice>Filters were updated. Click Get Info to refresh preview.</PreviewNotice>
          )}

          {isPreviewLoading ? null : previewStatistics.length > 0 ? (
            <PreviewTableWrapper>
              <PreviewTable>
                <thead>
                  <tr>
                    <th>Landing</th>
                    <th>Page Loads</th>
                    <th>Registrations</th>
                    <th>Ratio</th>
                    <th>Diff Main</th>
                    <th style={{ width: '60px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {previewStatistics.map((landing, index) => {
                    const pageloads = Number(landing.page_load || 0);
                    const registrations = Number(landing.registrations || 0);
                    const ratio = pageloads > 0 ? registrations / pageloads : 0;
                    const baseLanding = previewStatistics[0];
                    const basePageloads = Number(baseLanding?.page_load || 0);
                    const baseRegistrations = Number(baseLanding?.registrations || 0);
                    const baseRatio = basePageloads > 0 ? baseRegistrations / basePageloads : 0;
                    let diffMain = null;

                    if (index !== 0) {
                      if (baseRatio === 0) {
                        diffMain = ratio === 0 ? 0 : Number.POSITIVE_INFINITY;
                      } else {
                        diffMain = ratio / baseRatio - 1;
                      }
                    }

                    const diffDisplay = diffMain === null
                      ? '-'
                      : Number.isFinite(diffMain)
                        ? `${diffMain >= 0 ? '+' : ''}${(diffMain * 100).toFixed(2)}%`
                        : '+∞%';

                    const landingKey = getLandingKey(landing);
                    const landingKeyStr = landingKey?.toString() || '';
                    const hasImage = landingImages instanceof Map && landingImages.has(landingKeyStr);

                    return (
                      <tr key={landingKey}>
                        <td>{landing.url || landing.name}</td>
                        <td>{pageloads}</td>
                        <td>{registrations}</td>
                        <td>{(ratio * 100).toFixed(2)}%</td>
                        <td>{diffDisplay}</td>
                        <td style={{ textAlign: 'center' }}>
                          <ImageButtonsWrapper>
                            {hasImage && (
                              <ImageDeleteButton
                                type="button"
                                onClick={() => handleImageDeleteClick(landingKeyStr)}
                                title="Remove image"
                              >
                                <DeleteIcon />
                              </ImageDeleteButton>
                            )}
                            <ImageUploadButton
                              type="button"
                              onClick={() => handleImageButtonClick(landingKeyStr)}
                              title={hasImage ? 'Change image' : 'Upload image'}
                            >
                              {hasImage ? (
                                <CheckCircleIcon style={{ color: '#10b981' }} />
                              ) : (
                                <CloudUploadIcon />
                              )}
                            </ImageUploadButton>
                          </ImageButtonsWrapper>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </PreviewTable>
            </PreviewTableWrapper>
          ) : (
            <PreviewEmptyState>Use Get Info to preview landing statistics before creating the test.</PreviewEmptyState>
          )}

          <FieldBlock style={{ marginTop: '24px', gridColumn: '1 / -1' }}>
            <FieldLabel htmlFor="ab-test-comment">Comment</FieldLabel>
            <TextArea
              id="ab-test-comment"
              placeholder="Enter comment (optional)"
              value={comment}
              onChange={(event) => {
                onCommentChange(event.target.value);
              }}
            />
          </FieldBlock>
        </ModalBody>

        <ModalFooter>
          <div />
          <ActionsGroup>
            <SubmitButton type="button" onClick={onGetInfo} disabled={isPreviewLoading}>
              {isPreviewLoading ? (
                <>
                  <Preloader />
                  Loading...
                </>
              ) : (
                'Get Info'
              )}
            </SubmitButton>
            <SecondaryButton type="button" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <SubmitButton type="button" onClick={onCreate} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Preloader />
                  Creating...
                </>
              ) : (
                'Create Test'
              )}
            </SubmitButton>
          </ActionsGroup>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreateAbTestModal;
